import { useState, useEffect, useCallback } from 'react'
import api from '../../lib/axios'
import { PageHeader, Card, Spinner, EmptyState, StatusBadge } from '../../components/ui'
import ShareModal          from '../../components/sharing/ShareModal'
import VersionHistoryPanel from '../../components/sharing/VersionHistoryPanel'
import DiffStatusBanner    from '../../components/diff/DiffStatusBanner'

function fmt(iso) {
  return iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''
}

const TYPE_COLORS = {
  functional: 'text-pink-300 bg-pink-500/10',
  technical:  'text-blue-300 bg-blue-500/10',
  api:        'text-amber-300 bg-amber-500/10',
  user_story: 'text-emerald-300 bg-emerald-500/10',
}

export default function Specifications() {
  const [specs, setSpecs]           = useState([])
  const [loading, setLoading]       = useState(true)
  const [shareDoc, setShareDoc]     = useState(null)
  const [historyDoc, setHistoryDoc] = useState(null)

  const load = useCallback(() => {
    setLoading(true)
    api.get('/specifications').then(r => {
      const data = r.data?.data ?? r.data
      setSpecs(Array.isArray(data) ? data : [])
    }).finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  return (
    <div className="pb-10 animate-fade-in" style={{ background: 'linear-gradient(180deg,#0d0521 0%,#07020f 100%)', minHeight: '100%' }}>
      <PageHeader title="Specifications" subtitle={`${specs.length} generated`} />
      <div className="px-8">
        {loading ? (
          <div className="flex items-center justify-center h-48"><Spinner size="lg" /></div>
        ) : specs.length === 0 ? (
          <Card><EmptyState icon="◈" title="No specifications yet"
            description="Auto-generate functional, technical, or API specifications from your codebase via the automation panel." /></Card>
        ) : (
          <div className="space-y-3">
            {specs.map(spec => (
              <div key={spec.id} className="glass-card overflow-hidden">

                {spec.status === 'complete' && spec.last_generated_commit_sha && (
                  <DiffStatusBanner type="specification" id={spec.id} onRegenQueued={load} />
                )}

                <div className="flex items-center gap-4 px-5 py-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <p className="text-sm font-semibold text-white truncate">{spec.title}</p>
                      {spec.generation_mode === 'diff' && (
                        <span className="text-[9px] font-bold uppercase text-blue-400 bg-blue-500/10 px-1.5 py-0.5 rounded-full border border-blue-500/20">DIFF</span>
                      )}
                      {spec.is_public && (
                        <span className="text-[9px] font-bold uppercase text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded-full border border-emerald-500/20">SHARED</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <StatusBadge status={spec.status} />
                      {spec.spec_type && (
                        <span className={`text-xs capitalize px-2 py-0.5 rounded-full ${TYPE_COLORS[spec.spec_type] ?? 'text-white/40 bg-white/5'}`}>
                          {spec.spec_type.replace('_', ' ')}
                        </span>
                      )}
                      <span className="text-xs text-white/35">{spec.repo_name}</span>
                      <span className="text-xs text-white/25">v{spec.version ?? 1}</span>
                      {spec.section_count && <span className="text-xs text-white/25">{spec.section_count} sections</span>}
                      {spec.view_count > 0 && <span className="text-xs text-white/25">👁 {spec.view_count}</span>}
                      <span className="text-xs text-white/25">{fmt(spec.created_at)}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                    {spec.status === 'complete' && (
                      <>
                        <button onClick={() => setHistoryDoc(historyDoc?.id === spec.id ? null : spec)}
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white/50 text-xs font-medium hover:bg-white/10 transition-all">
                          🕐 History
                        </button>
                        <button onClick={() => setShareDoc(spec)}
                          className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-all ${
                            spec.is_public
                              ? 'bg-emerald-500/10 border-emerald-500/25 text-emerald-300'
                              : 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10'}`}>
                          🔗 Share
                        </button>
                        {spec.public_url && (
                          <a href={spec.public_url} target="_blank" rel="noreferrer"
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-purple-500/10 border border-purple-500/20 text-purple-300 text-xs font-medium hover:bg-purple-500/20 transition-all">
                            ↓ Download
                          </a>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {historyDoc?.id === spec.id && (
                  <div className="border-t border-white/6 px-5 py-4">
                    <VersionHistoryPanel type="specification" id={spec.id} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {shareDoc && (
        <ShareModal type="specification" id={shareDoc.id} title={shareDoc.title}
          onClose={() => { setShareDoc(null); load() }} />
      )}
    </div>
  )
}
